import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import moment from 'moment';
import { useState, useEffect } from 'react';

var usaFormat = 'MM/DD/YYYY';
var globalFormat = 'YYYY/MM/DD';
var inputFormat = 'M/D/YYYY';
var outputFormat = 'MM/DD/YYYY';
var calendarHeaderFormat = 'MMMM YYYY';
var useStrictParse = true;
var todayMoment = moment();
var todayDate = todayMoment.format(outputFormat);
function formatDate(date, format) {
  // a default for passing into our utility functions as they all read USA style
  var dateFormat = format ? format : usaFormat;
  return moment(date).format(dateFormat);
}
function unsafeInitMoment(date, format) {
  if (format === globalFormat) {
    return moment(date, globalFormat, useStrictParse);
  }
  return moment(date, inputFormat, useStrictParse);
}
function initMoment(date, format) {
  var momentObj = date ? unsafeInitMoment(date, format) : moment();
  return momentObj.isValid() ? momentObj : moment();
}
function checkPastDate(date, before, format) {
  var pastDate = before ? initMoment(before, format) : todayMoment;
  return date && initMoment(date, format).isBefore(pastDate, 'day');
}
function checkFutureDate(date) {
  return date && initMoment(date).isAfter(todayMoment, 'day');
}
function checkWeekdayDate(date, format) {
  return date && initMoment(date, format).day() % 6 != 0;
}
function checkWeekendDate(date, format) {
  return date && initMoment(date, format).day() % 6 == 0;
}
function displayFormat(date, format) {
  return date ? initMoment(date).format(format) : '';
}
function headerFormat(activeMonth) {
  return activeMonth ? initMoment(activeMonth).format(calendarHeaderFormat) : '';
}
function checkValidDateFormat(str, format) {
  var regexFormat = /^\d{1,2}[/]\d{1,2}[/]\d{4}$/;
  if (format === "".concat(globalFormat)) {
    regexFormat = /^\d{4}[/]\d{1,2}[/]\d{1,2}$/;
  }
  if (!str) return true;
  return regexFormat.test(str) && unsafeInitMoment(str, format).isValid();
}
function checkSameDate(date1, date2) {
  // set dates to same format
  var formattedDate1 = formatDate(date1);
  var formattedDate2 = formatDate(date2);
  // then check for same date
  return formattedDate1 && formattedDate2 && checkValidDateFormat(formattedDate1) && checkValidDateFormat(formattedDate2) ? initMoment(formattedDate1).isSame(initMoment(formattedDate2), 'day') : false;
}
function checkDateInList(date, allowList) {
  for (var i = 0; i < allowList.length; i++) {
    if (checkSameDate((date === null || date === void 0 ? void 0 : date.value) || date, allowList[i].toString())) {
      return true;
    }
  }
  return false;
}
function checkToday(date) {
  return date ? checkSameDate(date, todayDate) : false;
}
function toActiveMonth(dateIn, type, format) {
  // format date to USA style format, then grab active month
  var momentObj = initMoment(formatDate(dateIn));
  if (type === 'prev') {
    momentObj.subtract(1, 'month');
  } else if (type === 'next') {
    momentObj.add(1, 'month');
  }
  // return it formatted as requested
  return formatDate(momentObj.startOf('month'), format);
}
function toDateObj(date, disableDates, format) {
  return {
    before: checkPastDate(date, disableDates === null || disableDates === void 0 ? void 0 : disableDates.before, format),
    dateNumber: date ? initMoment(date).date() : '',
    future: checkFutureDate(date),
    past: checkPastDate(date),
    value: date ? displayFormat(date) : '',
    weekday: checkWeekdayDate(date, format),
    weekend: checkWeekendDate(date, format)
  };
}
function chunk(arr, size) {
  return Array.from({
    length: Math.ceil(arr.length / size)
  }, function (v, i) {
    return arr.slice(i * size, i * size + size);
  });
}
function toMonthArray(activeMonth, disableDates, format) {
  var momentObj = initMoment(activeMonth).startOf('month');
  var dayNo = momentObj.day();
  var monthLength = momentObj.daysInMonth();
  var arr = Array(dayNo).fill(1);
  for (var i = 0; i < monthLength; i++, momentObj.add(1, 'day')) {
    arr.push(toDateObj(momentObj, disableDates, format));
  }
  return chunk(arr, 7);
}
function handleDateTransition(date, type, disableDates, format) {
  // Get the moment object from a date always formatted in MM/DD/YYYY
  var momentObj = initMoment(formatDate(date));
  // Iterate by one until we land on the next valid date
  do {
    if (type === 'prev') {
      momentObj.subtract(1, 'day');
    } else if (type === 'next') {
      momentObj.add(1, 'day');
    }
  } while (disableDates !== null && disableDates !== void 0 && disableDates.only && checkDateInList(momentObj.format('MM/DD/YYYY'), disableDates.only) || disableDates !== null && disableDates !== void 0 && disableDates.except && !checkDateInList(momentObj.format('MM/DD/YYYY'), disableDates.except) || disableDates !== null && disableDates !== void 0 && disableDates.weekdays && checkWeekdayDate(momentObj) || disableDates !== null && disableDates !== void 0 && disableDates.weekends && checkWeekendDate(momentObj));
  return formatDate(momentObj, format);
}
function getDate(str) {
  return (str.match('[-0-9/.\n]+') || []).pop() || '';
}

//Modified from https://usehooks.com/useKeyPress/
function useKeyPress(targetKey) {
  // State for keeping track of whether key is pressed
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    keyPressed = _useState2[0],
    setKeyPressed = _useState2[1];

  // Add event listeners
  useEffect(function () {
    // If pressed key is our target key then set to true
    function downHandler(_ref) {
      var keyCode = _ref.keyCode;
      if (keyCode === targetKey) {
        setKeyPressed(true);
      }
    }

    // If released key is our target key then set to false
    function upHandler(_ref2) {
      var keyCode = _ref2.keyCode;
      if (keyCode === targetKey) {
        setKeyPressed(false);
      }
    }
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return function () {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, [targetKey]);
  return keyPressed;
}

export { checkDateInList, checkFutureDate, checkPastDate, checkSameDate, checkToday, checkValidDateFormat, checkWeekdayDate, checkWeekendDate, displayFormat, formatDate, getDate, handleDateTransition, headerFormat, toActiveMonth, toDateObj, toMonthArray, useKeyPress };
